import { useCallback, useRef, useState, type TouchEvent } from 'react';

const TRIGGER_DISTANCE = 64;
const MAX_DISTANCE = 96;

interface Options {
  onRefresh: () => void | Promise<void>;
}

interface Result {
  pullDistance: number;
  refreshing: boolean;
  onTouchStart: (event: TouchEvent<HTMLElement>) => void;
  onTouchMove: (event: TouchEvent<HTMLElement>) => void;
  onTouchEnd: () => void;
}

/**
 * Adds pull-to-refresh to a vertically scrolling page. Pulling only starts
 * when the page is already at its top, so normal scrolling remains untouched.
 */
export function usePullToRefresh({ onRefresh }: Options): Result {
  const startY = useRef<number | null>(null);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const onTouchStart = useCallback((event: TouchEvent<HTMLElement>) => {
    if (refreshing || event.touches.length !== 1 || event.currentTarget.scrollTop > 0) {
      startY.current = null;
      return;
    }
    startY.current = event.touches[0]?.clientY ?? null;
  }, [refreshing]);

  const onTouchMove = useCallback((event: TouchEvent<HTMLElement>) => {
    if (startY.current === null || refreshing || event.touches.length !== 1) return;
    if (event.currentTarget.scrollTop > 0) {
      startY.current = null;
      setPullDistance(0);
      return;
    }
    const currentY = event.touches[0]?.clientY ?? startY.current;
    const distance = currentY - startY.current;
    if (distance <= 0) return;
    event.preventDefault();
    setPullDistance(Math.min(MAX_DISTANCE, distance));
  }, [refreshing]);

  const onTouchEnd = useCallback(() => {
    const shouldRefresh = pullDistance >= TRIGGER_DISTANCE && !refreshing;
    startY.current = null;
    setPullDistance(0);
    if (!shouldRefresh) return;

    setRefreshing(true);
    Promise.resolve(onRefresh()).finally(() => setRefreshing(false));
  }, [onRefresh, pullDistance, refreshing]);

  return { pullDistance, refreshing, onTouchStart, onTouchMove, onTouchEnd };
}

export function pullToRefreshMessage(
  pullDistance: number,
  refreshing: boolean,
): 'pull' | 'release' | 'refreshing' {
  if (refreshing) return 'refreshing';
  return pullDistance >= TRIGGER_DISTANCE ? 'release' : 'pull';
}
